import React, { useState } from "react";
import {
    Alert,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    TextField,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CloseIcon from "@mui/icons-material/Close";
import NuxButton from "../nux/components/buttons/NuxButton";
import {
    AccountInsightApiError,
    useAccountInsightClient,
} from "../client/AccountInsightClient";
import { CustomerNotificationSettings } from "../client/AccountInsightsTypes";
import { useTranslation } from "react-i18next";

const AdminCustomerNotificationSettingsDialog = () => {
    const { t } = useTranslation();
    const {
        getCustomerNotificationSettings,
        saveCustomerNotificationSettings,
    } = useAccountInsightClient();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState<
        CustomerNotificationSettings | undefined
    >(undefined);
    const [editedSettings, setEditedSettings] = useState<
        CustomerNotificationSettings | undefined
    >(undefined);
    const [message, setMessage] = useState<string | undefined>(undefined);

    const displayError = (reason: unknown) => {
        console.log("Error: ", reason);
        if (reason instanceof AccountInsightApiError) {
            setMessage(reason.message);
        } else if (reason instanceof Error) {
            setMessage(reason.message);
        } else {
            setMessage("Unknown error '" + reason + "'");
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setMessage(undefined);
        setLoading(true);
        getCustomerNotificationSettings()
            .then((value) => {
                const settingsWithDefaults = {
                    ...value,
                    includeLinkToSession: !!value.includeLinkToSession,
                };
                setSettings(settingsWithDefaults);
                setEditedSettings(settingsWithDefaults);
            })
            .catch(displayError)
            .finally(() => setLoading(false));
    };

    const handleClose = () => {
        if (saving) return;
        setEditedSettings(settings);
        setOpen(false);
        setMessage(undefined);
    };

    const handleCancel = () => {
        handleClose();
    };

    const handleSave = () => {
        if (!editedSettings) return;

        const updatedSettings = {
            ...editedSettings,
            notificationMailAddress:
                editedSettings.notificationMailAddress === ""
                    ? null
                    : editedSettings.notificationMailAddress,
        };

        setSaving(true);
        setMessage(undefined);
        saveCustomerNotificationSettings(updatedSettings)
            .then(() => {
                setSettings(updatedSettings);
                setOpen(false);
            })
            .catch(displayError)
            .finally(() => setSaving(false));
    };

    const handleNotificationMailAddressChange = (value: string) => {
        setEditedSettings((prevSettings) =>
            prevSettings
                ? {
                      ...prevSettings,
                      notificationMailAddress: value,
                  }
                : prevSettings
        );
    };

    const handleBooleanSettingChange = (
        fieldName: keyof Pick<
            CustomerNotificationSettings,
            | "sendMailToCustomerOnSuccess"
            | "sendMailToCustomerOnFailure"
            | "sendMailToInitiatorOnSuccess"
            | "sendMailToInitiatorOnFailure"
            | "includeLinkToSession"
        >,
        value: boolean
    ) => {
        setEditedSettings((prevSettings) =>
            prevSettings
                ? {
                      ...prevSettings,
                      [fieldName]: value,
                  }
                : prevSettings
        );
    };

    return (
        <>
            <NuxButton variant="outlined" size="small" onClick={handleOpen}>
                <AdminPanelSettingsIcon fontSize="small" />
                &nbsp;{t("admin")}
            </NuxButton>
            <Dialog onClose={handleClose} open={open} fullWidth maxWidth="sm">
                <DialogTitle sx={{ m: 0, pr: 6 }}>
                    {t("adminSettings")}
                    <IconButton
                        aria-label={t("dialogClose")}
                        onClick={handleClose}
                        disabled={saving}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t("adminSettingsOrganizationWarning")}
                    </Alert>
                    <DialogContentText>
                        {t("adminSettingsContent")}
                    </DialogContentText>
                    {message && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {message}
                        </Alert>
                    )}
                    {loading ? (
                        <CircularProgress sx={{ mt: 2 }} />
                    ) : (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            <TextField
                                margin="dense"
                                id="notificationMailAddress"
                                name="notificationMailAddress"
                                label={t("notificationMailAddress")}
                                type="email"
                                fullWidth
                                variant="outlined"
                                value={
                                    editedSettings?.notificationMailAddress ||
                                    ""
                                }
                                onChange={(event) =>
                                    handleNotificationMailAddressChange(
                                        event.currentTarget.value
                                    )
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            !!editedSettings?.sendMailToCustomerOnSuccess
                                        }
                                        onChange={(event) =>
                                            handleBooleanSettingChange(
                                                "sendMailToCustomerOnSuccess",
                                                event.currentTarget.checked
                                            )
                                        }
                                    />
                                }
                                label={t("sendMailToCustomerOnSuccess")}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            !!editedSettings?.sendMailToCustomerOnFailure
                                        }
                                        onChange={(event) =>
                                            handleBooleanSettingChange(
                                                "sendMailToCustomerOnFailure",
                                                event.currentTarget.checked
                                            )
                                        }
                                    />
                                }
                                label={t("sendMailToCustomerOnFailure")}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            !!editedSettings?.sendMailToInitiatorOnSuccess
                                        }
                                        onChange={(event) =>
                                            handleBooleanSettingChange(
                                                "sendMailToInitiatorOnSuccess",
                                                event.currentTarget.checked
                                            )
                                        }
                                    />
                                }
                                label={t("sendMailToInitiatorOnSuccess")}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            !!editedSettings?.sendMailToInitiatorOnFailure
                                        }
                                        onChange={(event) =>
                                            handleBooleanSettingChange(
                                                "sendMailToInitiatorOnFailure",
                                                event.currentTarget.checked
                                            )
                                        }
                                    />
                                }
                                label={t("sendMailToInitiatorOnFailure")}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            !!editedSettings?.includeLinkToSession
                                        }
                                        onChange={(event) =>
                                            handleBooleanSettingChange(
                                                "includeLinkToSession",
                                                event.currentTarget.checked
                                            )
                                        }
                                    />
                                }
                                label={t("includeLinkToSession")}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <NuxButton
                        onClick={handleSave}
                        disabled={loading || saving || !editedSettings}
                        color="success"
                        variant="contained"
                    >
                        {t("dialogSave")}
                    </NuxButton>
                    <NuxButton
                        onClick={handleCancel}
                        disabled={saving}
                        color="secondary"
                        variant="outlined"
                    >
                        {t("dialogCancel")}
                    </NuxButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdminCustomerNotificationSettingsDialog;
